import React, { useState } from "react";
import { toast } from "react-toastify";

import Modal from "../ui/Modal";
import PropertyForm from "./PropertyForm";
import { usePropertyStore } from "../../store/propertyStore";
import {
  useCreatePropertyMutation,
  useUpdatePropertyMutation,
  useExtractPdfDataMutation,
  useExtractFloorPlanDataMutation,
} from "../../queries/properties/usePropertiesQuery";

export default function PropertyFormModal({ agentId }) {
  const [isBrochureExtractDataOpen, setIsBrochureExtractDataOpen] =
    useState(false);
  const [isFloorPlanExtractDataOpen, setIsFloorPlanExtractDataOpen] =
    useState(false);

  const {
    selectedProperty,
    clearSelectedProperty,
    setExtractedPdfData,
    setExtractedFloorPlanData,
  } = usePropertyStore();

  const createMutation = useCreatePropertyMutation();
  const updateMutation = useUpdatePropertyMutation();

  const { mutate: extractPdfData, isPending: isExtractingPdfData } =
    useExtractPdfDataMutation();
  const { mutate: extractFloorPlanData, isPending: isFloorPlanExtracting } =
    useExtractFloorPlanDataMutation();

  const isEdit = !!selectedProperty?.id;
  const resolvedAgentId =
    agentId || selectedProperty?.agent_id || selectedProperty?.agentId;

  const isPending =
    createMutation.isPending ||
    updateMutation.isPending ||
    isExtractingPdfData ||
    isFloorPlanExtracting;

  const handleExtractPdfData = (BrochureFile) => {
    if (!BrochureFile) return;

    if (!(BrochureFile instanceof File)) {
      toast.error("Please Reupload the PDF file for extraction");
      return;
    }
    const formData = new FormData();
    formData.append("file", BrochureFile);
    extractPdfData(formData, {
      onSuccess: (data) => {
        setExtractedPdfData(data);
        setIsBrochureExtractDataOpen(true);
      },
      onError: (error) => {
        const errorMessage =
          typeof error === "string"
            ? error
            : error?.message || "Failed to extract PDF data";
        toast.error(errorMessage);
      },
    });
  };

  const handleExtractFloorPlanData = (FloorPlanFile) => {
    if (!FloorPlanFile) return;

    if (!(FloorPlanFile instanceof File)) {
      toast.error("Please Reupload the file for extraction");
      return;
    }
    const formData = new FormData();
    formData.append("file", FloorPlanFile);
    extractFloorPlanData(formData, {
      onSuccess: (data) => {
        setExtractedFloorPlanData(data);
        setIsFloorPlanExtractDataOpen(true);
      },
      onError: (error) => {
        const errorMessage =
          typeof error === "string"
            ? error
            : error?.message || "Failed to extract Floor Plan data";
        toast.error(errorMessage);
      },
    });
  };

  const handleSubmit = (payload) => {
    if (!resolvedAgentId) {
      toast.error("Agent is required to save this property");
      return;
    }

    if (isEdit) {
      updateMutation.mutate(
        {
          agentId: resolvedAgentId,
          propertyId: selectedProperty.id,
          data: payload,
        },
        {
          onSuccess: () => {
            toast.success("Property updated successfully");
            clearSelectedProperty();
          },
          onError: (error) => {
            const errorMessage =
              error.response?.data?.message ||
              error.message ||
              "Failed to update property";
            toast.error(errorMessage);
          },
        },
      );
    } else {
      createMutation.mutate(
        {
          agentId: resolvedAgentId,
          data: payload,
        },
        {
          onSuccess: () => {
            toast.success("Property created successfully");
            clearSelectedProperty();
          },
          onError: (error) => {
            const errorMessage =
              error.response?.data?.message ||
              error.message ||
              "Failed to create property";
            toast.error(errorMessage);
          },
        },
      );
    }
  };

  return (
    <Modal
      isOpen={!!selectedProperty}
      onClose={clearSelectedProperty}
      className="property-form-modal"
      title={isEdit ? "Edit Property" : "Add Property"}
    >
      {selectedProperty && (
        <PropertyForm
          setIsBrochureExtractDataOpen={setIsBrochureExtractDataOpen}
          handleExtractPdfData={handleExtractPdfData}
          isBrochureExtractDataOpen={isBrochureExtractDataOpen}
          isFloorPlanExtractDataOpen={isFloorPlanExtractDataOpen}
          setIsFloorPlanExtractDataOpen={setIsFloorPlanExtractDataOpen}
          onCloseBrochureExtractData={() => setIsBrochureExtractDataOpen(false)}
          onCloseFloorPlanExtractData={() =>
            setIsFloorPlanExtractDataOpen(false)
          }
          isFloorPlanExtracting={isFloorPlanExtracting}
          initialData={selectedProperty || {}}
          handleExtractFloorPlanData={handleExtractFloorPlanData}
          onSubmit={handleSubmit}
          isPDFExtracting={isExtractingPdfData}
          isLoading={isPending}
          onCancel={clearSelectedProperty}
        />
      )}
    </Modal>
  );
}
